import { JSX, lazy, Suspense, createSignal, createEffect } from 'solid-js';
import { Route, Router } from '@solidjs/router';
import { TerminalHeader } from './components/common/TerminalHeader';
import { Scanlines } from './components/common/Scanlines';
import { SessionRecoveryBar } from './components/common/SessionRecoveryBar';
import { AuthProvider, useAuthenticatedUser } from './contexts/AuthContext';
import { useNavigate } from '@solidjs/router';
import { CommandPalette } from './components/CommandPalette';
import { createCommandRegistry } from './commands/commandRegistry';
import { createLogoutCommand, createSettingsCommand } from './commands/systemCommands';
import { useKeyboardShortcuts } from './utils/useKeyboardShortcuts';
import LogoutConfirmationDialog from './components/common/LogoutConfirmationDialog';
import ComingSoonDialog from './components/common/ComingSoonDialog';

// Lazy-loaded components for code splitting
const AuthPage = lazy(() => import('./pages/AuthPage'));
const Lobby = lazy(() => import('./pages/Lobby'));
const GameSession = lazy(() => import('./pages/GameSession'));

const NotFoundPage = () => (
	<div class="flex min-h-screen items-center justify-center bg-black text-green">
		<div class="text-center">
			<h1 class="mb-4 text-4xl font-bold">404</h1>
			<p class="text-lg">Page not found</p>
		</div>
	</div>
);

const ProtectedPage = (props: { children: JSX.Element }) => {
	const { user, isLoading, authError } = useAuthenticatedUser();
	const navigate = useNavigate();

	createEffect(() => {
		// Redirect if not loading and (no user OR auth error occurred)
		if (!isLoading() && (!user() || authError())) {
			navigate('/', { replace: true });
		}
	});

	return (
		<>
			{isLoading() && <div class="flex h-full w-full items-center justify-center">Loading...</div>}
			{!isLoading() && authError() && <div class="flex h-full w-full items-center justify-center">Redirecting...</div>}
			{!isLoading() && !authError() && !user() && (
				<div class="flex h-full w-full items-center justify-center">Redirecting...</div>
			)}
			{!isLoading() && !authError() && user() && props.children}
		</>
	);
};

const ProtectedLobby = () => (
	<ProtectedPage>
		<Suspense fallback={<div class="flex h-full w-full items-center justify-center">Loading lobby...</div>}>
			<Lobby />
		</Suspense>
	</ProtectedPage>
);

const ProtectedGameSession = () => (
	<ProtectedPage>
		<Suspense fallback={<div class="flex h-full w-full items-center justify-center">Loading game...</div>}>
			<GameSession />
		</Suspense>
	</ProtectedPage>
);

const Layout = (props: { children?: JSX.Element }) => {
	const [isCommandPaletteOpen, setIsCommandPaletteOpen] = createSignal(false);
	const [isLogoutDialogOpen, setIsLogoutDialogOpen] = createSignal(false);
	const [isComingSoonDialogOpen, setIsComingSoonDialogOpen] = createSignal(false);

	// Create command registry
	const commandRegistry = createCommandRegistry();

	// Register system commands
	createEffect(() => {
		const logoutCommand = createLogoutCommand({
			onCommandPaletteClose: () => setIsCommandPaletteOpen(false),
			onShowLogoutDialog: () => setIsLogoutDialogOpen(true),
		});
		commandRegistry.register(logoutCommand);

		const settingsCommand = createSettingsCommand({
			onCommandPaletteClose: () => setIsCommandPaletteOpen(false),
			onShowComingSoonDialog: () => setIsComingSoonDialogOpen(true),
		});
		commandRegistry.register(settingsCommand);
	});

	// Setup keyboard shortcuts for command palette
	useKeyboardShortcuts([
		{
			key: 'k',
			modifiers: { ctrl: true, shift: false, alt: false, meta: false },
			action: () => setIsCommandPaletteOpen(true),
			description: 'Open command palette',
		},
		{
			key: 'k',
			modifiers: { meta: true, shift: false, alt: false, ctrl: false },
			action: () => setIsCommandPaletteOpen(true),
			description: 'Open command palette (Mac)',
		},
	]);

	return (
		<div
			class="flex h-screen w-screen flex-col overflow-hidden bg-gray p-4 font-mono text-green sm:p-6 md:text-xl lg:p-8 lg:text-2xl"
			data-testid="app-root"
		>
			<Scanlines />
			<div class="flex h-full w-full flex-col rounded-lg bg-black p-2 shadow-lg">
				<header class="w-full">
					<TerminalHeader />
				</header>
				<main class="mx-auto flex h-full w-full flex-col">{props.children}</main>
				<SessionRecoveryBar />
				<footer class="mx-auto py-2 text-sm opacity-75">
					<p>© 2025 Pandemic Poker</p>
				</footer>
			</div>

			{/* Command Palette */}
			<CommandPalette
				isOpen={isCommandPaletteOpen()}
				onClose={() => setIsCommandPaletteOpen(false)}
				commands={commandRegistry.getAll()}
			/>

			{/* Logout Confirmation Dialog */}
			<LogoutConfirmationDialog isOpen={isLogoutDialogOpen()} onClose={() => setIsLogoutDialogOpen(false)} />

			{/* Coming Soon Dialog */}
			<ComingSoonDialog isOpen={isComingSoonDialogOpen()} onClose={() => setIsComingSoonDialogOpen(false)} />
		</div>
	);
};
export default () => (
	<AuthProvider>
		<Router root={Layout}>
			<Route path="/" component={AuthPage} />
			<Route path="/auth" component={AuthPage} />
			<Route path="/lobby" component={ProtectedLobby} />
			<Route path="/game/:sessionId" component={ProtectedGameSession} />

			<Route path="*" component={NotFoundPage} />
		</Router>
	</AuthProvider>
);
